// ==========================================
// NoResultsModal.js - Reusable modal for no search results
// ==========================================
import React from 'react';
import { X, Search, MapPin, AlertCircle } from 'lucide-react';

const NoResultsModal = ({ isOpen, onClose, searchParams }) => {
  if (!isOpen) return null;

  const suggestions = [
    {
      icon: <MapPin className="text-blue-500" size={20} />,
      title: 'Broaden Your Location',
      description: 'Increase your search radius or try searching in nearby cities'
    },
    {
      icon: <Search className="text-purple-500" size={20} />,
      title: 'Remove Description Filter',
      description: 'Try searching without specific description keywords'
    },
    {
      icon: <AlertCircle className="text-orange-500" size={20} />,
      title: 'Try Different Pet Type',
      description: 'Search for "All Animals" instead of a specific type'
    },
    {
      icon: <Search className="text-green-500" size={20} />,
      title: 'Remove Name Filter',
      description: 'Try searching without entering a pet name'
    },
    {
      icon: <MapPin className="text-red-500" size={20} />,
      title: 'Search Exact Location',
      description: 'Use the exact city, state, or zip code where the pet was lost/found'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-purple-500 text-white p-6 rounded-t-2xl">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                <Search size={28} />
                No Matches Found
              </h2>
              <p className="text-blue-100 text-sm">
                We couldn't find any pets matching your search criteria
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-full transition"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Current Search Info */}
        {searchParams && (
          <div className="p-6 bg-gray-50 border-b">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Your Current Search:</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {searchParams.animal_type && (
                <div className="bg-white p-2 rounded border border-gray-200">
                  <span className="text-gray-600">Type: </span>
                  <span className="font-semibold">{searchParams.animal_type}</span>
                </div>
              )}
              {searchParams.location && (
                <div className="bg-white p-2 rounded border border-gray-200">
                  <span className="text-gray-600">Location: </span>
                  <span className="font-semibold">{searchParams.location}</span>
                </div>
              )}
              {searchParams.radius_km && (
                <div className="bg-white p-2 rounded border border-gray-200">
                  <span className="text-gray-600">Radius: </span>
                  <span className="font-semibold">{searchParams.radius_km} km</span>
                </div>
              )}
              {searchParams.category && searchParams.category !== 'all' && (
                <div className="bg-white p-2 rounded border border-gray-200">
                  <span className="text-gray-600">Category: </span>
                  <span className="font-semibold">{searchParams.category}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Suggestions */}
        <div className="p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">💡 Try These Suggestions:</h3>
          <div className="space-y-4">
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border-2 border-blue-100 hover:border-blue-300 transition"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                  {suggestion.icon}
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-800 mb-1">{suggestion.title}</h4>
                  <p className="text-sm text-gray-600">{suggestion.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Tips */}
        <div className="p-6 bg-yellow-50 border-t border-yellow-200">
          <h3 className="text-sm font-bold text-yellow-900 mb-2 flex items-center gap-2">
            ⚠️ Important Tips
          </h3>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• Pets may travel far from where they were lost - try expanding your search radius</li>
            <li>• Check neighboring cities and states</li>
            <li>• New posts are added frequently - try searching again later</li>
            <li>• Consider posting your own "Missing" or "Found" report to reach more people</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="p-6 border-t flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-semibold"
          >
            Modify Search
          </button>
          <button
            onClick={() => {
              onClose();
              // Optionally scroll to top
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoResultsModal;
